import {
    AfterViewInit,
    Directive,
    ElementRef,
    OnDestroy,
    Renderer2,
    ViewChild,
} from "@angular/core";

import { Context } from "@hermes/app-core";

import { DrupalTrayService } from "../drupal-tray-service/drupal-tray.service";

/**
 * Common behavior for Drupal Trays (Loading, Show/Hide, handle iframe, ...)
 */
@Directive()
export abstract class DrupalTrayDirective implements AfterViewInit, OnDestroy {
    @ViewChild("frame")
    public frameRef: ElementRef<HTMLIFrameElement>;

    public drupalTrayName: string;
    public drupalTrayTitle: string;

    public set trayName(v: string) {
        // Expected value : tray-[name]
        const [, drupalTrayName] = v.split("tray-");
        this.drupalTrayName = drupalTrayName;
        this.updateTrayTitle();
    }

    public iframeOpacity: number = 0;

    constructor(
        private window: Window,
        private document: Document,
        private context: Context,
        private renderer: Renderer2,
        private drupalTrayService: DrupalTrayService,
    ) {}

    // This method is implemented inside each specific Tray component
    protected abstract trayInit: () => void;

    public ngAfterViewInit(): void {
        if (this.context.isInBrowserMode()) {
            this.window.addEventListener(
                `drupalTrayInit-${this.drupalTrayName}`,
                this.trayInit,
            );
        }
    }

    public ngOnDestroy(): void {
        if (this.context.isInBrowserMode()) {
            this.drupalTrayService.onUpdateTrayTitleOnError(false);
            this.window.removeEventListener(
                `drupalTrayInit-${this.drupalTrayName}`,
                this.trayInit,
            );
        }
    }

    /**
     * At tray loading we need to Inject events into the Iframe due to
     * the separation of opening tray event dealt by Angular and display
     * events dealt by Drupal.
     *
     * @param additionalScript Specific behavior (binding button, send analytics, retrieve data, ...)
     * @param additionalStyle Specific style
     */
    protected loadScript(
        additionalScript: string,
        additionalStyle?: string,
    ): void {
        this.addStyles(additionalStyle);
        this.addTextSpacing();
        this.addScripts(additionalScript);
        this.iframeOpacity = 1;
    }

    /**
     * Inject script in Drupal tray iframe. Common script is retrieve from DrupalTrayService.
     *
     * @param additionalScript Specific behavior to add (binding button, send analytics, retrieve data, ...)
     */
    private addScripts(additionalScript: string) {
        const commonScript = this.drupalTrayService.commonDrupalTrayScript(
            this.drupalTrayName,
        );
        const script: HTMLScriptElement = this.renderer.createElement("script");
        const text = this.renderer.createText(
            `(function() {\n${commonScript}\n${additionalScript}\n})();`,
        );
        this.renderer.appendChild(script, text);
        this.renderer.appendChild(
            this.frameRef.nativeElement.contentDocument.body,
            script,
        );
    }

    /**
     * Inject style in Drupal tray iframe. Common style is handled by Drupal.
     *
     * @param additionalStyle (Optional) Specific style to add
     */
    private addStyles(additionalStyle?: string) {
        const commonStyle = this.drupalTrayService.commonDrupalTrayStyle();
        const style = this.renderer.createElement("style");
        const styleText = this.renderer.createText(
            `${commonStyle}\n${additionalStyle || ""}`,
        );
        this.renderer.appendChild(style, styleText);
        this.renderer.appendChild(
            this.frameRef.nativeElement.contentWindow.document.body,
            style,
        );
    }

    /**
     * Overloads the text-spacing bookmarklet used to meet WCAG 2.1 Success Criterion 1.4.12 Text Spacing
     */
    private addTextSpacing() {
        /**
         * Applies text-spacing style to the iframe when it's applied on parent,
         * according to the following tools :
         * - https://www.html5accessibility.com/tests/tsbookmarklet.html
         * - 'Increase text spacing' option in the a11y.css addon (firefox - chrome)
         */
        if (
            this.document.body.classList.contains("text-spacing") ||
            this.document.querySelector("#a11ycss_textspacing_css")
        ) {
            this.frameRef.nativeElement.contentDocument.body.classList.add(
                "text-spacing",
            );
        }
    }

    private updateTrayTitle(): void {
        this.drupalTrayTitle = this.drupalTrayName;
        if (this.drupalTrayName === "subscribe") {
            this.drupalTrayTitle = $localize`:@@hermes.accessibility.forms.checkout.newsletter.title:Subscribe to the newsletter`;
        }
    }
}
